import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAllCompaniesService } from "../../services/Company/CompanyServices";
import { getAllProductCategoriesService } from "../../services/Product/ProductCategory/ProductServices";
import {
  createProductService,
  deleteProductService,
  getAllproductsService,
  updateProductService,
} from "../../services/Product/ProductServices";
import { getAllProductTypesService } from "../../services/Product/ProductType/ProductServices";
import { getAllProductImage } from "../../services/ProductsImages/ProductsImages";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface NamedRef {
  _id: string;
  name: string;
}

interface GalleryImage {
  key: string;
  filename: string;
}

interface AdminProduct {
  _id: string;
  name: string;
  description: string;
  link?: string;
  category: NamedRef;
  type?: NamedRef;
  allergens: string[];
  picture: any[];
  price: number;
  discount: number;
  available: boolean;
  companies: NamedRef[];
}

const emptyProduct = (): AdminProduct => ({
  _id: "",
  name: "",
  description: "",
  link: "",
  category: { _id: "", name: "" },
  type: undefined,
  allergens: [],
  picture: [],
  price: 0,
  discount: 0,
  available: true,
  companies: [],
});

const AdminProducts = () => {
  const { t } = useTranslation(["common", "error"]);
  const toast = useRef<Toast>(null);

  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [categories, setCategories] = useState<NamedRef[]>([]);
  const [types, setTypes] = useState<NamedRef[]>([]);
  const [companies, setCompanies] = useState<NamedRef[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [availableFilter, setAvailableFilter] = useState<boolean | null>(null);

  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null);
  const [creatingProduct, setCreatingProduct] = useState<AdminProduct | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<AdminProduct | null>(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      getAllproductsService(),
      getAllProductCategoriesService(),
      getAllProductTypesService(),
      getAllCompaniesService(),
      getAllProductImage(),
    ])
      .then(([productsRes, categoriesRes, typesRes, companiesRes, imagesRes]: any[]) => {
        setProducts(productsRes.data ?? []);
        setCategories(categoriesRes.data ?? []);
        setTypes(typesRes.data ?? []);
        setCompanies(companiesRes.data ?? []);
        setGalleryImages(imagesRes.data ?? []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredProducts = useMemo(() => {
    const search = globalFilterValue.trim().toLowerCase();
    return products.filter((product) => {
      if (categoryFilter && product.category?._id !== categoryFilter) return false;
      if (typeFilter && product.type?._id !== typeFilter) return false;
      if (availableFilter != null && product.available !== availableFilter) return false;
      if (
        search &&
        !product.name.toLowerCase().includes(search) &&
        !product.description.toLowerCase().includes(search)
      )
        return false;
      return true;
    });
  }, [products, globalFilterValue, categoryFilter, typeFilter, availableFilter]);

  const validateProduct = (candidate: AdminProduct) => {
    if (!candidate.name.trim()) {
      showError(toast, t("error:missingField"));
      return false;
    }
    if (!candidate.category?._id) {
      showError(toast, t("error:missingField"));
      return false;
    }
    if (candidate.price < 0) {
      showError(toast, t("error:missingField"));
      return false;
    }
    return true;
  };

  const submitEdit = () => {
    if (!editingProduct) return;
    if (!validateProduct(editingProduct)) return;
    updateProductService({
      _id: editingProduct._id,
      name: editingProduct.name,
      description: editingProduct.description,
      link: editingProduct.link,
      category: editingProduct.category._id,
      type: editingProduct.type?._id,
      allergens: editingProduct.allergens ?? [],
      picture: editingProduct.picture ?? [],
      price: editingProduct.price,
      discount: editingProduct.discount,
      available: editingProduct.available,
      companies: editingProduct.companies.map(e => e._id),
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingProduct(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const submitCreate = () => {
    if (!creatingProduct) return;
    if (!validateProduct(creatingProduct)) return;
    createProductService({
      name: creatingProduct.name,
      description: creatingProduct.description,
      link: creatingProduct.link,
      category: creatingProduct.category._id,
      type: creatingProduct.type?._id,
      allergens: creatingProduct.allergens ?? [],
      picture: creatingProduct.picture ?? [],
      price: creatingProduct.price,
      discount: creatingProduct.discount,
      available: creatingProduct.available,
      companies: creatingProduct.companies.map(e => e._id),
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setCreatingProduct(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingProduct) return;
    deleteProductService({ _id: deletingProduct._id })
      .then(() => {
        setProducts((prev) => prev.filter((p) => p._id !== deletingProduct._id));
        setDeletingProduct(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const renderProductFields = (
    value: AdminProduct,
    onChange: (next: AdminProduct) => void
  ) => (
    <div className="p-fluid">
      <div className="field">
        <label>{t("common:Name")}</label>
        <InputText
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </div>
      <div className="field">
        <label>{t("common:Description")}</label>
        <InputTextarea
          rows={4}
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
        />
      </div>
      <div className="field">
        <label>{t("common:link")}</label>
        <InputText
          value={value.link ?? ""}
          onChange={(e) => onChange({ ...value, link: e.target.value })}
        />
      </div>
      <div className="field">
        <label>{t("common:productCat")}</label>
        <Dropdown
          value={value.category?._id}
          options={categories.map((c) => ({ value: c._id, label: c.name }))}
          onChange={(e) =>
            onChange({
              ...value,
              category: categories.find((c) => c._id === e.value) as NamedRef,
            })
          }
        />
      </div>
      <div className="field">
        <label>{t("common:prodType")}</label>
        <Dropdown
          value={value.type?._id ?? null}
          options={types.map((tp) => ({ value: tp._id, label: tp.name }))}
          onChange={(e) =>
            onChange({
              ...value,
              type: e.value ? (types.find((tp) => tp._id === e.value) as NamedRef) : undefined,
            })
          }
          showClear
        />
      </div>
      <div className="field">
        <label>{t("common:allergens")}</label>
        <Chips
          value={value.allergens ?? []}
          onChange={(e) => onChange({ ...value, allergens: e.value ?? [] })}
        />
      </div>
      <div className="field">
        <label>{t("common:price")}</label>
        <InputNumber
          value={value.price}
          onValueChange={(e) => onChange({ ...value, price: e.value ?? 0 })}
          mode="currency"
          currency="EUR"
          locale="it-IT"
          min={0}
        />
      </div>
      <div className="field">
        <label>{t("common:discount")}</label>
        <InputNumber
          value={value.discount}
          onValueChange={(e) => onChange({ ...value, discount: e.value ?? 0 })}
          min={0}
          max={100}
        />
      </div>
      <div className="field flex align-items-center" style={{ gap: "0.5rem" }}>
        <Checkbox
          inputId="available"
          checked={value.available}
          onChange={(e) => onChange({ ...value, available: !!e.checked })}
        />
        <label htmlFor="available">{t("common:available")}</label>
      </div>
      <div className="field">
        <label>{t("common:imageProd")}</label>
        <MultiSelect
          value={value.picture.map((p) => p.key)}
          options={galleryImages.map((img) => ({ value: img.key, label: img.filename }))}
          onChange={(e) =>
            onChange({
              ...value,
              picture: galleryImages.filter((img) => (e.value as string[]).includes(img.key)),
            })
          }
          display="chip"
          filter
        />
      </div>
      <div className="field">
        <label>{t("common:companies")}</label>
        <MultiSelect
          value={value.companies.map((c) => c._id)}
          options={companies.map((c) => ({ value: c._id, label: c.name }))}
          onChange={(e) =>
            onChange({
              ...value,
              companies: companies.filter((c) => (e.value as string[]).includes(c._id)),
            })
          }
          display="chip"
        />
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader
        title={t("common:products")}
        action={
          <Button className="general-button" onClick={() => setCreatingProduct(emptyProduct())}>
            {t("common:newProduct")}
          </Button>
        }
      />

      <div className="admin-page-body">
      <div className="flex flex-wrap align-items-center mb-3" style={{ gap: "1rem" }}>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={(e) => setGlobalFilterValue(e.target.value)}
            placeholder="Keyword Search"
          />
        </span>
        <Dropdown
          value={categoryFilter}
          options={categories.map((c) => ({ value: c._id, label: c.name }))}
          onChange={(e) => setCategoryFilter(e.value)}
          placeholder={t("common:productCat")}
          showClear
        />
        <Dropdown
          value={typeFilter}
          options={types.map((tp) => ({ value: tp._id, label: tp.name }))}
          onChange={(e) => setTypeFilter(e.value)}
          placeholder={t("common:prodType")}
          showClear
        />
        <Dropdown
          value={availableFilter}
          options={[
            { value: true, label: t("common:Yes") },
            { value: false, label: t("common:No") },
          ]}
          onChange={(e) => setAvailableFilter(e.value)}
          placeholder={t("common:available")}
          showClear
        />
      </div>

      <DataTable
        value={filteredProducts}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        sortField="name"
        sortOrder={1}
        emptyMessage={t("common:noResults")}
      >
        <Column field="name" header={t("common:Name")} sortable />
        <Column
          field="category.name"
          header={t("common:productCat")}
          body={(row: AdminProduct) => row.category?.name}
          sortable
        />
        <Column
          field="type.name"
          header={t("common:prodType")}
          body={(row: AdminProduct) => row.type?.name ?? "-"}
        />
        <Column
          field="company.name"
          header={t("common:companies")}
          body={(row: AdminProduct) => {
              if (!row.companies || row.companies.length === 0) return "-";
              return row.companies.map(c => c.name).join(", ");
          }}
        />
        <Column field="price" header={t("common:price")} sortable />
        <Column
          field="available"
          header={t("common:available")}
          body={(row: AdminProduct) => (row.available ? t("common:Yes") : t("common:No"))}
        />
        <Column
          body={(row: AdminProduct) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingProduct({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingProduct(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={t("common:newProduct")}
        visible={creatingProduct != null}
        onHide={() => setCreatingProduct(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setCreatingProduct(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitCreate}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {creatingProduct && renderProductFields(creatingProduct, setCreatingProduct)}
      </Dialog>

      <Dialog
        header={editingProduct?.name}
        visible={editingProduct != null}
        onHide={() => setEditingProduct(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingProduct(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingProduct && renderProductFields(editingProduct, setEditingProduct)}
      </Dialog>

      <Dialog
        onHide={() => setDeletingProduct(null)}
        visible={deletingProduct != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingProduct(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingProduct?.name}
      </Dialog>
    </div>
  );
};

export default AdminProducts;
